<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactPreferenceDefinitionsAPI from 'dashboard/api/contactPreferenceDefinitions';

export default {
  components: {
    NextButton,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    nextPosition: {
      type: Number,
      default: 10,
    },
  },
  emits: ['created'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      preferenceKey: '',
      fieldType: 'text',
      optionsText: '',
      show: true,
      isCreating: false,
    };
  },
  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    preferenceKey: {
      required,
      isKey(value) {
        return /^[a-z0-9_]+$/.test(value);
      },
    },
  },
  computed: {
    keyErrorMessage() {
      if (!this.v$.preferenceKey.isKey) {
        return this.$t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.KEY.ERROR');
    },
    fieldTypeOptions() {
      return [
        {
          value: 'text',
          label: this.$t('CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.text'),
        },
        {
          value: 'list',
          label: this.$t('CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.list'),
        },
        {
          value: 'list_with_custom',
          label: this.$t(
            'CONTACT_PREFERENCES_SETTINGS.FIELD_TYPES.list_with_custom'
          ),
        },
      ];
    },
    requiresOptions() {
      return ['list', 'list_with_custom'].includes(this.fieldType);
    },
    normalizedOptions() {
      return this.optionsText
        .split('\n')
        .map(option => option.trim())
        .filter(Boolean)
        .filter((option, index, options) => options.indexOf(option) === index);
    },
    isButtonDisabled() {
      return (
        this.v$.$invalid ||
        (this.requiresOptions && !this.normalizedOptions.length) ||
        this.isCreating
      );
    },
  },
  methods: {
    onDisplayNameChange() {
      this.preferenceKey = convertToAttributeSlug(this.displayName);
    },
    async addPreference() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isCreating = true;
      try {
        const response = await ContactPreferenceDefinitionsAPI.create({
          label: this.displayName,
          key: this.preferenceKey,
          field_type: this.fieldType,
          options: this.requiresOptions ? this.normalizedOptions : [],
          enabled_for_contacts: true,
          enabled_for_close_circle_members: true,
          active: true,
          position: this.nextPosition,
        });
        this.$emit('created', response.data);
        useAlert(this.$t('CONTACT_PREFERENCES_SETTINGS.ADD.API.SUCCESS'));
        this.onClose();
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('CONTACT_PREFERENCES_SETTINGS.ADD.API.ERROR')
        );
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONTACT_PREFERENCES_SETTINGS.ADD.TITLE')"
      />

      <form class="flex w-full" @submit.prevent="addPreference">
        <div class="w-full">
          <woot-input
            v-model="displayName"
            :label="$t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.NAME.LABEL')"
            type="text"
            :class="{ error: v$.displayName.$error }"
            :error="
              v$.displayName.$error
                ? $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.NAME.ERROR')
                : ''
            "
            :placeholder="
              $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.NAME.PLACEHOLDER')
            "
            @update:model-value="onDisplayNameChange"
            @blur="v$.displayName.$touch"
          />
          <woot-input
            v-model="preferenceKey"
            :label="$t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.KEY.LABEL')"
            type="text"
            :class="{ error: v$.preferenceKey.$error }"
            :error="v$.preferenceKey.$error ? keyErrorMessage : ''"
            :placeholder="
              $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.KEY.PLACEHOLDER')
            "
            @blur="v$.preferenceKey.$touch"
          />
          <label class="mb-1 block text-sm font-medium text-n-slate-12">
            {{ $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.TYPE.LABEL') }}
          </label>
          <div class="relative mb-4">
            <select
              v-model="fieldType"
              class="block h-10 w-full m-0 appearance-none bg-none border-0 rounded-lg bg-n-alpha-black2 px-3 py-2 pr-8 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            >
              <option
                v-for="option in fieldTypeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down size-3.5 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
            />
          </div>
          <div v-if="requiresOptions" class="mb-4">
            <label class="mb-1 block text-sm font-medium text-n-slate-12">
              {{ $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.OPTIONS.LABEL') }}
            </label>
            <textarea
              v-model="optionsText"
              rows="5"
              class="m-0 w-full resize-none rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak placeholder:text-n-slate-10 focus:outline-n-brand"
              :placeholder="
                $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.OPTIONS.PLACEHOLDER')
              "
            />
            <p
              v-if="!normalizedOptions.length"
              class="mb-0 mt-1 text-xs text-n-ruby-9"
            >
              {{ $t('CONTACT_PREFERENCES_SETTINGS.ADD.FORM.OPTIONS.ERROR') }}
            </p>
          </div>
          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('CONTACT_PREFERENCES_SETTINGS.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="onClose"
            />
            <NextButton
              type="submit"
              :label="$t('CONTACT_PREFERENCES_SETTINGS.ADD.SUBMIT')"
              :is-loading="isCreating"
              :disabled="isButtonDisabled"
            />
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
